import React from 'react';
import { Fab } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface FloatingActionButtonProps {
  /**
   * The text label to display on the button
   */
  label?: string;
  /**
   * The route to navigate to when clicked
   */
  navigateTo?: string;
  /**
   * Custom click handler (overrides navigation if provided)
   */
  onClick?: () => void;
  /**
   * Whether to show the button in extended format with label
   */
  extended?: boolean;
  /**
   * Custom aria-label for accessibility
   */
  ariaLabel?: string;
}

/**
 * FloatingActionButton - A reusable floating action button component
 * 
 * Features:
 * - Positioned in bottom-right corner with consistent styling
 * - Can navigate to a route or execute custom onClick handler
 * - Supports both circular and extended (with label) variants
 * - Uses application theme colors and hover effects
 * - Includes proper accessibility attributes
 */
export const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  label = 'Create Ticket',
  navigateTo = '/tickets/create',
  onClick,
  extended = true,
  ariaLabel
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (navigateTo) {
      navigate(navigateTo);
    }
  };

  return (
    <Fab
      variant={extended ? 'extended' : 'circular'}
      color="primary"
      aria-label={ariaLabel || (extended ? label : 'Create new item')}
      onClick={handleClick}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1000,
        backgroundColor: '#162346',
        boxShadow: '0 4px 12px rgba(22, 35, 70, 0.3)',
        '&:hover': {
          backgroundColor: '#1a2a52',
          boxShadow: '0 6px 16px rgba(22, 35, 70, 0.4)',
        }
      }}
    >
      <AddIcon sx={extended ? { mr: 1 } : undefined} />
      {extended && label}
    </Fab>
  );
};

export default FloatingActionButton;